import type { AlvoStatus, StatusAlvo } from '../../../api/monitoramento';

export type GrupoId = 'pdpj' | 'tribunais';
export type RamoJusticaId =
  | 'estadual'
  | 'federal'
  | 'trabalho'
  | 'eleitoral'
  | 'militar'
  | 'outros';

export interface RamoConfig {
  id: RamoJusticaId;
  label: string;
}

export const RAMOS: RamoConfig[] = [
  { id: 'estadual', label: 'Estadual' },
  { id: 'federal', label: 'Federal' },
  { id: 'trabalho', label: 'Trabalho' },
  { id: 'eleitoral', label: 'Eleitoral' },
  { id: 'militar', label: 'Militar' },
  { id: 'outros', label: 'Outros' },
];

export interface GrupoConfig {
  id: GrupoId;
  label: string;
  descricao: string;
  /** Se false, a seção nasce colapsada no primeiro render. */
  abertoPorDefault: boolean;
}

export const GRUPOS: GrupoConfig[] = [
  {
    id: 'pdpj',
    label: 'PDPJ / CNJ',
    descricao: 'Serviços externos mantidos pelo CNJ.',
    abertoPorDefault: true,
  },
  {
    id: 'tribunais',
    label: 'Tribunais',
    descricao: 'Sistemas eletrônicos dos tribunais (PJe, EPROC etc.).',
    abertoPorDefault: false,
  },
];

/**
 * O alvo `autenticador` é filtrado antes de chegar aqui (vai para a bolinha
 * de status no cabeçalho), então a função só precisa distinguir tribunais de
 * serviços do PDPJ. Qualquer alvo desconhecido cai em 'pdpj' por padrão.
 */
export function classificarGrupo(alvo: string): GrupoId {
  if (alvo.startsWith('tribunal.')) return 'tribunais';
  return 'pdpj';
}

/**
 * Deriva o ramo da justiça a partir da sigla do tribunal embutida no alvo.
 * Formato esperado: `tribunal.{sistema}.{sigla}`, ex.: `tribunal.pje.tjsp`.
 *
 * Regras:
 * - `tj<uf>`              → Estadual (TJSP, TJRJ, TJDFT…)
 * - `trf<n>` / `stj|stf`  → Federal
 * - `trt<n>` / `tst`      → Trabalho
 * - `tre<uf>` / `tse`     → Eleitoral
 * - `stm` / `tjm<uf>`     → Militar
 * - resto                 → Outros
 */
export function classificarRamoTribunal(alvo: string): RamoJusticaId {
  const partes = alvo.split('.');
  const sigla = (partes[2] ?? '').toLowerCase();
  if (!sigla) return 'outros';
  if (sigla === 'stj' || sigla === 'stf' || /^trf\d+$/.test(sigla)) return 'federal';
  if (sigla === 'tst' || /^trt\d+$/.test(sigla)) return 'trabalho';
  if (sigla === 'tse' || /^tre[a-z]{2}$/.test(sigla)) return 'eleitoral';
  if (sigla === 'stm' || /^tjm[a-z]{2}$/.test(sigla)) return 'militar';
  if (/^tj([a-z]{2}|dft)$/.test(sigla)) return 'estadual';
  return 'outros';
}

export interface Resumo {
  total: number;
  ativo: number;
  degradado: number;
  indisponivel: number;
  semDados: number;
  comIncidente: number;
}

export function resumir(alvos: AlvoStatus[]): Resumo {
  const r: Resumo = {
    total: alvos.length,
    ativo: 0,
    degradado: 0,
    indisponivel: 0,
    semDados: 0,
    comIncidente: 0,
  };
  for (const a of alvos) {
    if (a.status === 'ATIVO') r.ativo++;
    else if (a.status === 'DEGRADADO') r.degradado++;
    else if (a.status === 'INDISPONIVEL') r.indisponivel++;
    else if (a.status === 'SEM_DADOS') r.semDados++;
    if (a.incidente_ativo_id != null) r.comIncidente++;
  }
  return r;
}

/**
 * "Problema" = falha observada ou incidente aberto. SEM_DADOS é tratado como
 * indeterminado, NÃO como problema — não queremos que ausência de coleta
 * (ex.: synthetic não rodou) inflame o filtro "Só problemas".
 */
export function alvoComProblema(alvo: AlvoStatus): boolean {
  return (
    alvo.status === 'DEGRADADO' ||
    alvo.status === 'INDISPONIVEL' ||
    alvo.incidente_ativo_id != null
  );
}

/**
 * Peso usado na ordenação: alvos com problema sobem para o topo (maior peso).
 * SEM_DADOS fica abaixo de problemas observados mas acima de ATIVO, para
 * sinalizar "vale a pena olhar" sem alarmar.
 */
export function pesoAlvo(status: StatusAlvo, comIncidente: boolean): number {
  if (status === 'INDISPONIVEL') return 4;
  if (status === 'DEGRADADO') return 3;
  if (comIncidente) return 2;
  if (status === 'SEM_DADOS') return 1;
  return 0;
}

export function ordenarAlvos(alvos: AlvoStatus[]): AlvoStatus[] {
  return [...alvos].sort((a, b) => {
    const pa = pesoAlvo(a.status, a.incidente_ativo_id != null);
    const pb = pesoAlvo(b.status, b.incidente_ativo_id != null);
    if (pa !== pb) return pb - pa;
    return a.alvo.localeCompare(b.alvo);
  });
}

export interface AgrupamentoTribunais {
  ramo: RamoConfig;
  alvos: AlvoStatus[];
  resumo: Resumo;
}

export function agruparTribunaisPorRamo(tribunais: AlvoStatus[]): AgrupamentoTribunais[] {
  const porRamo = new Map<RamoJusticaId, AlvoStatus[]>();
  for (const a of tribunais) {
    const r = classificarRamoTribunal(a.alvo);
    if (!porRamo.has(r)) porRamo.set(r, []);
    porRamo.get(r)!.push(a);
  }
  // Respeita a ordem declarada em RAMOS (Estadual → Federal → Trabalho …)
  const resultado: AgrupamentoTribunais[] = [];
  for (const ramo of RAMOS) {
    const lista = porRamo.get(ramo.id);
    if (!lista || lista.length === 0) continue;
    resultado.push({ ramo, alvos: ordenarAlvos(lista), resumo: resumir(lista) });
  }
  return resultado;
}
